<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import CsatRatingSection from './CsatRatingSection.vue';
import CsatImprovementSection from './CsatImprovementSection.vue';
import CsatCommentSection from './CsatCommentSection.vue';
import CsatActions from './CsatActions.vue';
import type { RatingValue } from './RatingEmoji.types';
import type { CsatFormData } from '../types/csat.types';
import { ChatRateMode } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { devLog } from '@/common/debug';

const MIN_RATING_FOR_IMPROVEMENT = 4;

const props = defineProps<{
  entityType?: ChatRateMode;
}>();

const emit = defineEmits<{
  (event: 'rating-selected', value: CsatFormData): void;
  (event: 'submit', data: CsatFormData): void;
  (event: 'skip'): void;
  (
    event: 'feedback-option',
    payload: { feedbackOption: number; title: string; selected: boolean; type?: ChatRateMode }
  ): void;
}>();

const mockedFlowStore = useMockedFlowStore();
const {
  isActive: isMockedFlowActive,
  hasSelectedImprovements: hasMockedSelectedImprovements,
  selectedImprovements: mockedSelectedImprovements,
} = storeToRefs(mockedFlowStore);

const selectedRating = ref<RatingValue | null>(null);
const selectedImprovements = ref<string[]>([]);
const comment = ref('');

// Initialize with mocked data if in mocked flow
onMounted(() => {
  if (isMockedFlowActive.value && hasMockedSelectedImprovements.value && mockedSelectedImprovements.value) {
    devLog.debug('[CSAT_FORM] Using mocked selected improvements', mockedSelectedImprovements.value);
    selectedRating.value = 1;
    selectedImprovements.value = [...mockedSelectedImprovements.value];
  }
});

const showImprovementSection = computed(() => {
  return selectedRating.value !== null && selectedRating.value < MIN_RATING_FOR_IMPROVEMENT;
});

const handleRatingSelected = (value: RatingValue) => {
  selectedRating.value = value;
  if (value >= MIN_RATING_FOR_IMPROVEMENT) {
    selectedImprovements.value = [];
  }
  emit('rating-selected', { rating: value, selectedImprovements: selectedImprovements.value, comment: comment.value });
};

const handleImprovementToggled = (payload: { id: string; label: string }) => {
  const index = selectedImprovements.value.indexOf(payload.id);
  const selected = index === -1;

  if (index === -1) {
    selectedImprovements.value.push(payload.id);
  } else {
    selectedImprovements.value.splice(index, 1);
  }

  emit('feedback-option', {
    feedbackOption: Number(payload.id),
    title: payload.label,
    selected,
    type: props.entityType,
  });
};

const handleSubmit = () => {
  emit('submit', {
    rating: selectedRating.value,
    selectedImprovements: selectedImprovements.value,
    comment: comment.value,
  });
};

const handleSkip = () => {
  emit('skip');
};

const handleCommentInput = (value: string) => {
  comment.value = value;
};
</script>

<template>
  <div class="csat-form">
    <CsatRatingSection :selected-rating="selectedRating" @rating-selected="handleRatingSelected" />

    <CsatImprovementSection
      v-if="showImprovementSection"
      :entity-type="entityType"
      :selected-improvements="selectedImprovements"
      @improvement-toggled="handleImprovementToggled"
    />

    <CsatCommentSection :value="comment" @input="handleCommentInput" />

    <CsatActions :selected-rating="selectedRating" @submit="handleSubmit" @skip="handleSkip" />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/mixin.scss';

.csat-form {
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;

  @include respond(phone) {
    padding: 16px 12px;
    max-width: 100%;
  }
}
</style>
